import React, { useMemo, ReactElement, ComponentProps } from 'react';
import { SimpleDialog } from '@grubhubprod/gh-cookbook-react/dist';
import { useConfirmDialog } from 'src/components/enterprise/features/Employee/components/ConfirmDialog/hooks/useConfirmDialog';

type SimpleDialogProps = ComponentProps<typeof SimpleDialog>;

export interface ConfirmDialogProps
  extends Omit<
    SimpleDialogProps,
    'isOpen' | 'onDismiss' | 'primaryButtonProps' | 'secondaryButtonProps'
  > {
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  children: ReactElement;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmFlavor = 'default',
  direction = 'row',
  width = '352px',
  isLoading = false,
  onConfirm,
  onCancel,
  children,
  ...simpleDialogProps
}) => {
  const { isOpen, handleOpen, handleConfirm, handleCancel } = useConfirmDialog({
    onConfirm,
    onCancel,
  });

  const trigger = useMemo(
    () =>
      React.cloneElement(children, {
        onClick: handleOpen,
      }),
    [children, handleOpen]
  );

  return (
    <>
      {trigger}
      <SimpleDialog
        {...(simpleDialogProps as SimpleDialogProps)}
        as="div"
        zindex={1030}
        aria-label={`${simpleDialogProps.title} dialog`}
        direction={direction}
        width={width}
        isOpen={isOpen}
        onDismiss={handleCancel}
        primaryButtonProps={{
          label: confirmLabel,
          flavor: confirmFlavor,
          isLoading,
          disabled: isLoading,
          onClick: handleConfirm,
        }}
        secondaryButtonProps={{
          label: cancelLabel,
          disabled: isLoading,
          onClick: handleCancel,
        }}
      />
    </>
  );
};
